import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Typography, Drawer, List, ListItem, ListItemIcon, ListItemText, ListSubheader, IconButton, Box, AppBar, Toolbar, Divider, Tooltip } from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import { useNavigate } from 'react-router-dom';
import UserProfile from '../common/UserProfile';
import { useAuth } from '../../contexts/AuthContext';
import { useAlert } from '../../contexts/AlertContext';
import { useAbility } from '../../contexts/AbilityContext';
import { useDrawer } from '../../contexts/DrawerContext';
import { COLORS } from '../../constants/radar';
import { getNavigationGroups } from './navigationItems';
import ActivitiesCard from './ActivitiesCard';
import TeamPendingActionsCard from './TeamPendingActionsCard';
import FixedTermContractsCard from './FixedTermContractsCard';
import AssignmentCompletionCard from './AssignmentCompletionCard';
import { Container } from '../common/styles';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, impersonationChangeKey } = useAuth();
  const { showAlert } = useAlert();
  const ability = useAbility();
  const { drawerOpen, toggleDrawer, scrollPosition, setScrollPosition } = useDrawer();
  const drawerRef = useRef(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Force re-render when impersonation changes
  useEffect(() => {
    setRefreshKey(prev => prev + 1);
  }, [impersonationChangeKey]);

  // Restore scroll position when returning to the page
  useEffect(() => {
    if (drawerRef.current && scrollPosition > 0) {
      drawerRef.current.scrollTop = scrollPosition;
    }
  }, []);

  // Save scroll position on scroll
  const handleDrawerScroll = useCallback((e) => {
    setScrollPosition(e.target.scrollTop);
  }, [setScrollPosition]);

  const handleCardClick = (card) => {
    if (!card.disabled) {
      if (card.id === 'employee-radar') {
        if (!user?.employee?.id) {
          showAlert('User not found', 'error');
          return;
        }
        navigate(`/employee-radar/${user.employee.id}`, {
          state: {
            user: {
              id: user.employee?.id,
              localAccountId: user.employee.azureId,
              username: user.employee.email,
              name: user.employee.fullName
            },
            fromLandingPage: true
          }
        });
      } else {
        navigate(card.path);
      }
    }
  };

  // Get filtered navigation groups based on permissions
  const filteredNavigationGroups = getNavigationGroups(ability);

  return (
    <Container sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      gap: 0,
      p: 3,
      height: '100vh'
    }}>
      <AppBar 
        position="static" 
        elevation={0}
        sx={{ 
          backgroundColor: 'white',
          color: 'primary.main',
          borderBottom: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          height: '72px',
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
          mb: 3
        }}
      >
        <Toolbar sx={{ 
          justifyContent: 'space-between',
          minHeight: '72px',
          px: 3,
          py: 2
        }}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: 2
          }}>
            <IconButton
              edge="start"
              color={COLORS.PRIMARY}
              aria-label="menu"
              onClick={toggleDrawer}
              sx={{ 
                width: 40,
                height: 40,
                borderRadius: 1,
                backgroundColor: 'background.default',
                '&:hover': {
                  backgroundColor: 'background.paper'
                }
              }}
            >
              {drawerOpen ? <MenuOpenIcon /> : <MenuIcon />}
            </IconButton>
            <Typography 
              variant="h6" 
              component="div"
              sx={{
                fontWeight: 600,
                fontSize: '1.25rem',
                color: COLORS.PRIMARY,
                position: 'relative',
                pl: 2,
                '&:before': {
                  content: '""',
                  position: 'absolute',
                  left: 0,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  height: '24px',
                  width: '4px',
                  backgroundColor: COLORS.PRIMARY,
                  borderRadius: '2px'
                }
              }}
            >
              My Software Mind
            </Typography>
          </Box>
          <Box sx={{ width: 'auto' }}>
            <UserProfile />
          </Box>
        </Toolbar>
      </AppBar>

      <Box sx={{ 
        display: 'flex',
        gap: 3,
        flex: 1,
        minHeight: 0
      }}>
        <Box
          sx={{
            width: drawerOpen ? 330 : 80,
            flexShrink: 0,
            transition: 'width 0.3s ease-in-out',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          <Drawer
            variant="permanent"
            open={drawerOpen}
            slotProps={{
              paper: {
                ref: drawerRef,
                onScroll: handleDrawerScroll
              }
            }}
            sx={{
              width: '100%',
              flex: 1,
              minHeight: 0,
              '& .MuiDrawer-paper': {
                position: 'relative',
                width: '100%',
                transition: 'none',
                bgcolor: 'background.default',
                overflowY: 'auto',
                overflowX: 'hidden',
                height: '100%',
                top: 'auto',
                borderRadius: 2,
                zIndex: 1000, // Ensure it's below the impersonation banner (1300)
                '& .MuiListSubheader-root': {
                  backgroundColor: COLORS.BACKGROUND_LIGHT,
                  py: 1.5,
                  px: 2,
                  lineHeight: '1.2',
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  letterSpacing: '0.1px',
                  textTransform: 'uppercase',
                  color: COLORS.PRIMARY,
                  borderTop: '1px solid',
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  mt: 2,
                  display: drawerOpen ? 'block' : 'none'
                }
              }
            }}
          >
          <Box sx={{ pt: drawerOpen ? 0 : 2 }}>
            {filteredNavigationGroups.map((group, index) => {
              const elements = [];
              
              // Add divider if needed
              if (!drawerOpen && index > 0) {
                elements.push(
                  <Divider 
                    key={`divider-${index}`}
                    sx={{ 
                      mx: 1.5, 
                      my: 2,
                      borderColor: COLORS.PRIMARY,
                      borderWidth: 2,
                      borderStyle: 'solid',
                      opacity: 0.3,
                      '&::before': {
                        borderWidth: 2,
                        borderColor: 'inherit'
                      }
                    }} 
                  />
                );
              }
              
              // Add the List
              elements.push(
                <List
                  key={group.title}
                  subheader={
                    drawerOpen ? (
                      <ListSubheader disableGutters disableSticky >
                        {group.title}
                      </ListSubheader>
                    ) : null
                  }
                  sx={{
                    py: 0,
                    '& .MuiListItem-root': {
                      px: drawerOpen ? 2 : 0,
                      py: 1,
                      '&:first-of-type': {
                        pt: drawerOpen ? 2 : 1
                      },
                      '&:last-of-type': {
                        pb: drawerOpen ? 2 : 1
                      }
                    }
                  }}
                >
                  {group.items.map((item) => {
                    if (item.isDivider) {
                      return (
                        <Divider 
                          key={item.id}
                          sx={{ 
                            mx: 2, 
                            my: 1,
                            borderColor: 'rgba(0, 0, 0, 0.12)'
                          }} 
                        />
                      );
                    }

                    const subItems = drawerOpen && item.subitems ? item.subitems.map((subitem) => (
                      <ListItem
                        key={subitem.id}
                        onClick={() => handleCardClick(subitem)}
                        sx={{
                          borderRadius: 0,
                          mx: 0,
                          mb: 0.5,
                          pl: 9,
                          pr: 3,
                          position: 'relative',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'rgba(0, 0, 0, 0.04)'
                          },
                          '&::before': {
                            content: '""',
                            position: 'absolute',
                            left: '32px',
                            top: '-12px',
                            bottom: '50%',
                            width: '2px',
                            backgroundColor: 'divider'
                          },
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            left: '32px',
                            top: '50%',
                            width: '20px',
                            height: '2px',
                            backgroundColor: 'divider'
                          }
                        }}
                      >
                        <ListItemIcon sx={{ 
                          minWidth: 36, 
                          color: COLORS.SECONDARY,
                          ml: 5
                        }}>
                          {subitem.icon}
                        </ListItemIcon>
                        <ListItemText 
                          primary={subitem.title}
                          secondary={subitem.description}
                          slotProps={{
                            primary: {
                              fontSize: '0.95rem',
                              fontWeight: 500,
                              color: COLORS.PRIMARY
                            },
                            secondary: {
                              fontSize: '0.85rem',
                              fontStyle: 'italic',
                              color: 'text.secondary'
                            }
                          }}
                        />
                      </ListItem>
                    )) : [];

                    return [
                      <Tooltip 
                        key={item.id}
                        title={!drawerOpen ? item.title : ''} 
                        placement="right"
                        arrow
                      >
                        <ListItem
                          onClick={() => handleCardClick(item)}
                          sx={{
                            borderRadius: 0,
                            mx: 0,
                            mb: 0.5,
                            cursor: 'pointer',
                            justifyContent: drawerOpen ? 'flex-start' : 'center',
                            px: drawerOpen ? 3 : 2,
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <ListItemIcon sx={{ 
                            minWidth: drawerOpen ? 40 : 'auto', 
                            color: COLORS.SECONDARY,
                            mr: drawerOpen ? 0 : 0
                          }}>
                            {item.icon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={item.title}
                            secondary={item.description}
                            sx={{
                              display: drawerOpen ? 'block' : 'none'
                            }}
                            slotProps={{
                              primary: {
                                fontSize: '1rem',
                                fontWeight: 500,
                                color: COLORS.PRIMARY 
                              },
                              secondary: {
                                fontSize: '0.775rem',
                                fontStyle: 'italic',
                                color: 'text.secondary'
                              }
                            }}
                          />
                        </ListItem>
                      </Tooltip>,
                      ...subItems
                    ];
                  })}
                </List>
              );
              
              return elements;
            })}
          </Box>
        </Drawer>
        </Box>

        <Box
          component="main"
          sx={{
            flex: 1,
            borderRadius: 2,
            backgroundColor: COLORS.WHITE,
            overflow: 'auto',
            p: 3
          }}
        >
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <ActivitiesCard key={refreshKey} />
            <TeamPendingActionsCard key={`team-${refreshKey}`} />
            <FixedTermContractsCard key={`ftc-${refreshKey}`} />
            <AssignmentCompletionCard key={`acr-${refreshKey}`} />
          </Box>
        </Box>
      </Box>
    </Container>
  );
};

export default LandingPage; 